import { type DefaultError, type QueryKey, type UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { queryClient } from '../query-client.ts'
import { type Impersonation, useImpersonation } from './impersonation.ts'

export const queryData = <TArgs extends unknown[], TData, TQueryKey extends QueryKey>(
	query: (...args: TArgs) => UseSuspenseQueryOptions<TData, DefaultError, TData, TQueryKey>,
) => ({
	ensure: (...args: TArgs) => queryClient.ensureQueryData(query(...args)),
	prefetch: (...args: TArgs) => queryClient.prefetchQuery(query(...args)),
	use: (...args: TArgs) => useSuspenseQuery(query(...args)),
	invalidate: (...args: TArgs) => queryClient.invalidateQueries({ queryKey: query(...args).queryKey }),
})

export const impersonatedQueryData = <TData, TQueryKey extends QueryKey>(
	query: (impersonation: Impersonation | undefined) => UseSuspenseQueryOptions<TData, DefaultError, TData, TQueryKey>,
) => ({
	ensure: ({ impersonation }: { impersonation: Impersonation | undefined }) => queryClient.ensureQueryData(query(impersonation)),
	fetch: ({ impersonation }: { impersonation: Impersonation | undefined }) => queryClient.query({ ...query(impersonation), staleTime: 0 }),
	use: () => useSuspenseQuery(query(useImpersonation())),
	invalidate: (impersonation: Impersonation | undefined) => queryClient.invalidateQueries({ queryKey: query(impersonation).queryKey }),
})
